/**
 * Freistell-Verfahren im Sticker-Studio.
 *
 * Jedes Verfahren liefert eine Maske: für jeden Bildpunkt einen Wert von 0
 * (weg) bis 255 (bleibt). Das eigentliche Anwenden passiert an einer Stelle in
 * `render.ts`, damit sich die Verfahren nicht gegenseitig in die Quere kommen.
 *
 * Die schweren Verfahren laden ihr Modell erst beim ersten Benutzen. Das
 * geschieht ausschließlich im Gerät – der Server ist daran nicht beteiligt und
 * es entstehen keine laufenden Kosten.
 */

export type EngineKey = 'tap' | 'tippen' | 'person' | 'face' | 'object' | 'birefnet' | 'tiefe';

/**
 * Wozu ein Modell da ist.
 *
 * Bis auf eines liefern hier alle eine Silhouette. „tiefe“ liefert
 * stattdessen eine Entfernung je Bildpunkt und gehört deshalb nicht in die
 * Liste der Freisteller – wohl aber in dieselbe Verwaltung: Es ist ein
 * Modell, das im Gerät läuft, einen Download kostet und einen Schalter
 * braucht, und diese drei Dinge stehen hier an einer Stelle statt an zweien.
 */
export type Zweck = 'freistellen' | 'tiefe';

/**
 * Welche Laufzeit ein Verfahren braucht.
 *
 * Wichtig für die Größenangabe: „Person“ und „Gesicht“ teilen sich wirklich
 * dieselbe Laufzeit. Wer eines von beiden benutzt hat, lädt beim anderen nur
 * noch das Modell – ein Fünftel Megabyte statt dreieinhalb.
 *
 * `onnx` und `onnx-gpu` teilen sie **nicht**, auch wenn beide „ONNX Runtime“
 * heissen: Der Einstiegspunkt `onnxruntime-web/wasm` lädt
 * `ort-wasm-simd-threaded.wasm`, `onnxruntime-web/webgpu` dagegen die
 * asyncify-Fassung. Zwei Dateien, zwei Downloads.
 *
 * Hier stand für beide `onnx`, und `downloadHint()` versprach deshalb, wer
 * „Niedrige Qualität“ schon habe, brauche für „Hohe Qualität“ nur noch das
 * Modell. Das stimmte nie – vorher waren es die jsep- statt der
 * asyncify-Fassung, also ebenfalls zwei verschiedene Dateien.
 */
export type RuntimeKind = 'keine' | 'mediapipe' | 'onnx' | 'onnx-gpu';

/**
 * Was die jeweilige Laufzeit einmalig kostet – komprimiert übertragen, MiB.
 *
 * Nachgemessen mit `gzip -c dist/assets/*.wasm`. Bei `.wasm` komprimiert
 * Caddy von sich aus (am laufenden Server geprüft: `content-encoding: gzip`),
 * hier ist also nichts vorzubereiten.
 */
export const RUNTIME_MB: Record<RuntimeKind, number> = {
  keine: 0,
  mediapipe: 3.3,
  onnx: 3.4,
  'onnx-gpu': 6.1,
};

export interface EngineInfo {
  key: EngineKey;
  label: string;
  /** Ein Satz, der erklärt, wofür das Verfahren taugt. */
  description: string;
  /**
   * Das Modell selbst, **so wie es über die Leitung geht**, in MiB.
   *
   * Also die komprimierte Grösse: `scripts/prepare-models.mjs` legt zu jedem
   * Modell über 1 MiB eine `.gz` daneben, und der Web-Caddy reicht sie mit
   * `precompressed gzip` durch. Ohne das lieferte er `.onnx` roh aus – Caddy
   * komprimiert unterwegs nur, was es nach dem Inhaltstyp dafür hält, und
   * `.onnx` kennt es nicht.
   *
   * Diese Zahl wird mit `RUNTIME_MB` addiert (`firstUseMb`) – beide müssen
   * deshalb dasselbe meinen.
   */
  modelMb: number;
  runtime: RuntimeKind;
  /** Ob es standardmäßig zur Verfügung steht. */
  defaultEnabled: boolean;
  /** Wofür es da ist. Ohne Angabe: freistellen. */
  zweck?: Zweck;
}

export interface CutoutEngine extends EngineInfo {
  /** Ob das Gerät die nötigen Bausteine überhaupt mitbringt. */
  supported: () => Promise<boolean>;
  /**
   * Berechnet die Maske. `seed` ist der angetippte Punkt, falls das Verfahren
   * einen braucht (Bildkoordinaten der Sticker-Fläche).
   */
  mask: (image: ImageData, seed?: { x: number; y: number }) => Promise<Uint8Array>;
}

/** Beschreibungen aller Verfahren – auch der abgeschalteten, für die Liste. */
export const ENGINE_INFO: EngineInfo[] = [
  {
    key: 'tap',
    label: 'Antippen',
    description: 'Tippe an, was bleiben soll. Ohne Download, funktioniert auf jedem Gerät.',
    modelMb: 0,
    runtime: 'keine',
    defaultEnabled: true,
  },
  {
    key: 'tippen',
    label: 'Antippen mit Netz',
    description:
      'Der Zusatz zu „Antippen“: Statt nach Farbe zu fluten, versteht ein Netz, was ein Gegenstand ist – Flasche antippen, Flasche kommt, samt Glanzlicht. Klassenfrei, also für Gegenstände wie für Personen. Der erste Tipp je Foto sieht sich das Bild an (rund eine Sekunde), jeder weitere kommt in Sekundenbruchteilen.',
    // Encoder 12,7 MB + Decoder 16,5 MB, beide roh (Caddy komprimiert .onnx
    // nur ueber die vorbereitete .gz, siehe prepare-models.mjs).
    modelMb: 26.9,
    runtime: 'onnx',
    defaultEnabled: false,
  },
  {
    key: 'person',
    label: 'Person',
    description: 'Erkennt Menschen und trennt sie vom Hintergrund.',
    modelMb: 0.24,
    runtime: 'mediapipe',
    defaultEnabled: true,
  },
  {
    key: 'face',
    label: 'Gesicht',
    description: 'Findet Gesichter und schneidet als Kopf zu – mit Stirn und Haaren.',
    modelMb: 0.22,
    runtime: 'mediapipe',
    defaultEnabled: true,
  },
  {
    key: 'object',
    label: 'Niedrige Qualität',
    description:
      'Stellt auch Gegenstände frei – Tasse, Hund, Blume. Kommt ohne Grafikeinheit aus und rechnet auf jedem Gerät in Sekunden; die Kante ist gröber als bei „Hohe Qualität“.',
    modelMb: 4.0,
    runtime: 'onnx',
    defaultEnabled: false,
  },
  {
    key: 'birefnet',
    label: 'Hohe Qualität',
    description:
      'Dasselbe wie „Niedrige Qualität“, nur deutlich genauer an Haaren, Zäunen und Brillenbügeln. Dafür der mit Abstand größte Download. Braucht zwingend eine Grafikeinheit: Die Gewichte liegen in halber Genauigkeit, dafür hat der Prozessorpfad keine Rechenwerke, und ein Bild würde dort Minuten dauern statt Sekunden. Fehlt sie, bleibt der Knopf abgeblendet.',
    // Nachgemessen an der Datei, die wirklich ausgeliefert wird:
    // public/models/birefnet-lite-512.onnx.gz = 82.082.888 B = 78,3 MiB.
    // (Nicht `gzip -9` von Hand nehmen – prepare-models.mjs komprimiert mit
    // einer anderen Stufe und kommt auf 48 KB mehr.)
    modelMb: 78.3,
    runtime: 'onnx-gpu',
    defaultEnabled: false,
  },
  {
    key: 'tiefe',
    label: 'Tiefenschärfe',
    description:
      'Schätzt für jeden Bildpunkt die Entfernung und lässt die Unschärfe damit WACHSEN, statt alles hinter dem Motiv gleich weich zu machen. Der Gewinn steckt in Bildern ohne freistellbares Motiv – Strasse, Tisch von schräg oben, Landschaft, eine Gruppe in mehreren Ebenen. Bei einem Porträt bleibt „Person“ die bessere Maske: Die Kante kann kein Tiefenmodell.',
    // Nachgemessen an der Datei, die ausgeliefert wird: 26,0 MiB roh,
    // 20,6 MiB als .gz – und die .gz reicht Caddy durch. (Nicht `gzip -9`
    // von Hand nehmen; prepare-models.mjs komprimiert mit einer anderen
    // Stufe und kommt auf ein paar Zehntel mehr.)
    modelMb: 20.6,
    runtime: 'onnx',
    defaultEnabled: false,
    zweck: 'tiefe',
  },
];

/** Was das Verfahren beim allerersten Benutzen kostet, in Megabyte. */
export function firstUseMb(info: EngineInfo): number {
  return Math.round((info.modelMb + RUNTIME_MB[info.runtime]) * 10) / 10;
}

/** Ein Satz zur Downloadgröße, so wie er in den Einstellungen steht. */
export function downloadHint(info: EngineInfo): string {
  if (info.runtime === 'keine') return 'Kein Download nötig.';
  const gesamt = firstUseMb(info).toLocaleString('de-DE');
  const geteilt = ENGINE_INFO.filter(
    (other) => other.runtime === info.runtime && other.key !== info.key,
  );
  const zusatz = info.modelMb.toLocaleString('de-DE');
  if (geteilt.length === 0) {
    return `Einmalig ${gesamt} MB laden, danach im Gerät gespeichert.`;
  }
  const namen = geteilt.map((other) => `„${other.label}“`).join(' und ');
  return `Einmalig ${gesamt} MB laden, danach im Gerät gespeichert. Zusammen mit ${namen} genutzt – wer das schon geladen hat, braucht hier nur noch ${zusatz} MB.`;
}
